import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Role → default dashboard mapping
const ROLE_DASHBOARD = {
  buyer:         '/buyer-operations-dashboard',
  seller:        '/sell-your-sugar',
  monitor:       '/monitor-dashboard',
  quota_manager: '/quota-allocation-pricing-center',
  transporter:   '/transporter-dashboard',
  factory_admin: '/factory-management-hub',
  sales_agent:   '/sales-agent-dashboard',
  admin:         '/admin-dashboard',
};

const Spinner = () => (
  <div style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#0d1f35',
  }}>
    <div style={{
      width: 32, height: 32,
      border: '3px solid #ff8c00',
      borderTopColor: 'transparent',
      borderRadius: '50%',
      animation: 'sh-spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes sh-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/**
 * ProtectedRoute
 * 
 * Props:
 *   element      — the component to render if access is allowed
 *   allowedRoles — array of role strings allowed (omit = any authenticated user)
 * 
 * Behaviour:
 *   - Loading auth state  → spinner (no flash redirect)
 *   - Not authenticated   → /login-authentication
 *   - Role mismatch       → user's own dashboard
 *   - Pass                → renders element
 */
const ProtectedRoute = ({ element, allowedRoles }) => {
  const { user, userProfile, loading, profileLoading } = useAuth();

  // 1. Wait for Supabase auth session to resolve
  if (loading) return <Spinner />;

  // 2. Not logged in at all
  if (!user) return <Navigate to="/login-authentication" replace />;

  // 3. Role check required — wait for profile to load before deciding
  if (allowedRoles && profileLoading) return <Spinner />;

  // 4. Profile loaded + role check
  // A user with role='buyer' and was_sales_agent=true (upgraded from sales
  // agent — see sales-agent-upgrade-to-buyer) is treated as also holding
  // 'sales_agent' for routes that allow it, so their dashboard access and
  // commission history remain reachable after the upgrade.
  const effectiveRoleMatch = allowedRoles && (
    allowedRoles.includes(userProfile?.role) ||
    (userProfile?.was_sales_agent && allowedRoles.includes('sales_agent'))
  );
  if (allowedRoles && userProfile?.role && !effectiveRoleMatch) {
    const redirectTo = ROLE_DASHBOARD[userProfile.role] || '/';
    return <Navigate to={redirectTo} replace />;
  }

  // 5. All checks passed
  return element;
};

export default ProtectedRoute;
